import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
};

export const candidateAPI = {
  getProfile: () => api.get('/candidates/profile'),
  updateProfile: (data) => api.put('/candidates/profile', data),
  addEducation: (data) => api.post('/candidates/education', data),
  addCertification: (data) => api.post('/candidates/certifications', data),
  addExperience: (data) => api.post('/candidates/experience', data),
  addSkill: (data) => api.post('/candidates/skills', data),
  deleteEducation: (id) => api.delete(`/candidates/education/${id}`),
  deleteCertification: (id) => api.delete(`/candidates/certifications/${id}`),
  deleteExperience: (id) => api.delete(`/candidates/experience/${id}`),
  deleteSkill: (id) => api.delete(`/candidates/skills/${id}`),
};

export const clientAPI = {
  getProfile: () => api.get('/clients/profile'),
  updateProfile: (data) => api.put('/clients/profile', data),
  updateLocalization: (data) => api.put('/clients/profile', data),
  searchCandidates: (params) => api.get('/clients/search', { params }),
  getCandidate: (candidateId) => api.get(`/clients/candidate/${candidateId}`),
  requestInfo: (candidateId, message) => api.post(`/clients/request-info/${candidateId}`, { message }),
  getRequests: () => api.get('/clients/requests'),
};

export const adminAPI = {
  getCandidates: (params) => api.get('/admin/candidates', { params }),
  getCandidate: (candidateId) => api.get(`/admin/candidate/${candidateId}`),
  updateCandidateApproval: (candidateId, data) => api.put(`/admin/candidate/${candidateId}/approval`, data),
  updateFieldVisibility: (candidateId, table, fieldId, data) =>
    api.put(`/admin/candidate/${candidateId}/visibility/${table}/${fieldId}`, data),
  getClients: (params) => api.get('/admin/clients', { params }),
  getClient: (clientId) => api.get(`/admin/client/${clientId}`),
  updateClient: (clientId, data) => api.put(`/admin/client/${clientId}`, data),
  getInformationRequests: (params) => api.get('/admin/information-requests', { params }),
  respondToRequest: (requestId, data) => api.put(`/admin/information-request/${requestId}`, data),
  getDashboard: () => api.get('/admin/dashboard'),
  getClientActivity: () => api.get('/admin/client-activity'),
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
  getFieldVisibilitySettings: () => api.get('/admin/field-visibility-settings'),
  updateFieldVisibilitySetting: (fieldId, data) => api.put(`/admin/field-visibility-settings/${fieldId}`, data),
  bulkUpdateFieldVisibilitySettings: async (updates) => {
    // Use multiple single API calls instead of bulk update
    const results = [];
    for (const update of updates) {
      try {
        const result = await api.put(`/admin/field-visibility-settings/${update.field_id}`, {
          is_visible_to_clients: update.is_visible_to_clients
        });
        results.push({ success: true, field_id: update.field_id, data: result.data });
      } catch (error) {
        results.push({ success: false, field_id: update.field_id, error: error.response?.data || error.message });
      }
    }
    return { data: { results } };
  },
  deleteCandidate: (candidateId) => api.delete(`/admin/candidates/${candidateId}`),
};

export const uploadAPI = {
  uploadCV: (formData) => {
    const uploadApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    uploadApi.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      }
    );

    return uploadApi.post('/upload/cv', formData);
  },
  downloadCV: (cvId) => {
    const token = localStorage.getItem('auth_token');
    return axios.get(`${API_BASE_URL}/upload/cv/${cvId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    });
  },
  deleteCV: (cvId) => api.delete(`/upload/cv/${cvId}`),
  reparseCV: (cvId) => api.post(`/upload/cv/${cvId}/reparse`),
};

export default api;